// ============================================================
// sculptKit.js — THE TOON + INK LOOK, borrowed from the IE sculpt kit
// ------------------------------------------------------------
// Vendored from gamemaster/models/sculpt/runtime/sculptedBody.jsx
// (caP'n's rule: ALWAYS borrow from Terror/Annihilation first).
// Same 3-band toon ramp, same inverted-hull ink, same lit-from-inside
// patch the Terror Plaza Fountain wears — so a TILT piece and a Plaza
// piece are the same species.
//
// ⚠ PALETTE LAW (TILT Law 6): this is a PF (inside-the-glass) file.
//   It imports no UI colour. Ever.
// ============================================================
import * as THREE from "three";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

let RAMP = null;
export const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};

const INKS = {};
export const inkMat = (width = 0.003, color = 0x161114) => {
  const k = width + ":" + color;
  if (INKS[k]) return INKS[k];
  const m = new THREE.MeshBasicMaterial({ color, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>\n  transformed += normalize( normal ) * ${width.toFixed(4)};`
    );
  };
  m.customProgramCacheKey = () => "ie-ink-" + width.toFixed(4);
  return (INKS[k] = m);
};

// Clone a loaded glTF scene and dress every mesh in the toon+ink look.
//   noInk  — /regex/ of mesh names that get no outline (eyes, glow)
//   glow   — { meshName: hexColour } meshes drawn flat and bright
//   ink    — outline width in MODEL units (scale-aware: pass width/scale)
// THE NAMING CONTRACT (same as Terror's fountain.glb):
//   glow_*  / bs_glow*  lit, unshaded, no outline  (bulbs, gems — the game pulses them)
//   glass_*             see-through, no outline
//   bs_*                a MOVING part: its origin is its pivot (the game turns it)
const GLOW_RE = /^(glow_|bs_glow)/;
const GLASS_RE = /^glass_/;

export function prepareSculpt(scene, { noInk = /eyes/, glow = {}, selfLight = 0.3, ink = 0.003, tweak } = {}) {
  const root = cloneSkinned(scene);
  const meshes = [], glowing = {}, mats = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    o.frustumCulled = false;
    if (GLOW_RE.test(o.name) && glow[o.name] === undefined) {
      o.material = new THREE.MeshBasicMaterial({ color: m.color, map: m.map || null, vertexColors: vc, toneMapped: false });
      glowing[o.name] = o;
      mats.push(o.material);
      return;
    }
    if (GLASS_RE.test(o.name)) {
      o.material = new THREE.MeshToonMaterial({ color: m.color, gradientMap: toonRamp(), transparent: true, opacity: 0.35, depthWrite: false });
      mats.push(o.material);
      return;
    }
    if (glow[o.name] !== undefined) {
      o.material = new THREE.MeshBasicMaterial({ color: glow[o.name], map: m.map || null, vertexColors: vc, toneMapped: false });
      glowing[o.name] = o;
      mats.push(o.material);
      return;
    }
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(selfLight), emissiveMap: m.map || null,
      transparent: true, opacity: 1,      // transparent ONCE (no recompiles); only opacity moves — see Willie's see-through fade
    });
    mats.push(o.material);
    if (vc) o.material.onBeforeCompile = (sh) => {
      sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;", "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
    };
    if (tweak) tweak(o);
    if (noInk.test(o.name)) return;
    const h = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat(ink)) : new THREE.Mesh(o.geometry, inkMat(ink));
    if (o.isSkinnedMesh) h.bind(o.skeleton, o.bindMatrix);
    h.name = o.name + "_ink"; h.frustumCulled = false;
    o.parent.add(h);
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) { B[o.name] = o; o.userData.p0 = o.position.clone(); } });
  return { root, B, glowing, mats };
}
